import React, { useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react'
import './Alert.css'

/**
 * Alert Component
 *
 * A reusable alert/notification component with auto-dismiss and close button
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Alert variant (success, error, warning, info)
 * @param {string} props.title - Alert title
 * @param {string} props.message - Alert message
 * @param {boolean} props.dismissible - Show close button (default: true)
 * @param {number} props.autoDismiss - Auto-dismiss after ms (0 = no auto-dismiss)
 * @param {Function} props.onClose - Close handler
 * @param {string} props.className - Additional CSS classes
 */
const Alert = ({
	variant = 'info',
	title,
	message,
	dismissible = true,
	autoDismiss = 0,
	onClose,
	className = '',
}) => {
	/**
	 * Auto-dismiss after specified time
	 */
	useEffect(() => {
		if (autoDismiss > 0 && onClose) {
			const timer = setTimeout(onClose, autoDismiss)
			return () => clearTimeout(timer)
		}
	}, [autoDismiss, onClose])

	/**
	 * Get icon based on variant
	 */
	const getIcon = () => {
		const iconProps = { size: 20 }

		switch (variant) {
			case 'success':
				return <CheckCircle {...iconProps} />
			case 'error':
				return <AlertCircle {...iconProps} />
			case 'warning':
				return <AlertTriangle {...iconProps} />
			case 'info':
			default:
				return <Info {...iconProps} />
		}
	}

	const alertClasses = ['alert', `alert--${variant}`, className].filter(Boolean).join(' ')

	return (
		<div className={alertClasses} role="alert">
			<div className="alert__icon">{getIcon()}</div>
			<div className="alert__content">
				{title && <div className="alert__title">{title}</div>}
				{message && <div className="alert__message">{message}</div>}
			</div>
			{dismissible && onClose && (
				<button className="alert__close" onClick={onClose} aria-label="Close alert">
					<X size={18} />
				</button>
			)}
		</div>
	)
}

export default Alert
